import React, { useState } from 'react'
import { Box, IconButton, Stack, Typography, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { useUser } from 'store'
import Modal from 'components/Modal'
import Switch from 'packages/ui/Switch'
import { User } from 'models/user.model'

import { ProfileCard, InfoCard, getCrownColor } from './styles/profile.styles'
import EditProfileModal from './EditProfileModal'
import ValidationCode, { Disclaimer } from 'pages/Auth/Login/pages/2FA'

const formatDate = (date?: string | null): string => {
  return date ? new Date(date).toLocaleDateString() : '-'
}

const ModalTwoFa: React.FC<{ onCancel: () => void, onValid: () => void }> = ({ onCancel, onValid }) => {
  const [stepCode, setStepCode] = useState<boolean>(false)
  return (
    <Modal onClose={onCancel}
    >
      {
        stepCode
          ? <ValidationCode onValid={onValid} />
          : <Disclaimer
            onSubmit={() => setStepCode(true)}
            onCancel={onCancel}
          />
      }

    </Modal>
  )
}
const ProfileSection: React.FC<{ user: User }> = ({ user }) => {
  const { t } = useTranslation()
  const [, setUser] = useUser()
  const theme = useTheme()
  const [twoFaOpen, setTwoFaOpen] = useState<boolean>(false)
  const [editOpen, setEditOpen] = useState<boolean>(false)

  return (
    <ProfileCard>
      {
        twoFaOpen && (
          <ModalTwoFa
            onCancel={() => setTwoFaOpen(false)}
            onValid={() => {
              setTwoFaOpen(false)
              setUser(prev => prev ? { ...prev, two_fa_activated: true } : prev)
            }}
          />
        )
      }
      {
        editOpen && (
          <EditProfileModal user={user} onClose={() => setEditOpen(false)} />
        )
      }
      <Stack spacing={2}>
        <InfoCard sx={{ position: 'relative' }}>
          {!user.demo_mode && (
            <IconButton
              aria-label={t('profile.edit.label')}
              onClick={() => setEditOpen(true)}
              size="small"
              sx={{ position: 'absolute', top: 12, right: 12, color: '#A2AAB8', fontSize: 14 }}>
              <i className="fa-light fa-pen" />
            </IconButton>
          )}
          <Stack spacing={2} sx={{ width: '100%' }}>
            {(user.admin || user?.roles?.admin_roles || user?.roles?.admin_contact) && (
              <Stack spacing={1} alignItems="center">
                <i className="fa-solid fa-crown" style={{ color: getCrownColor(user, theme) }} />
                <Typography variant="caption" sx={{ color: '#5D5D5D' }}>
                  {t('profile.full_management')}
                </Typography>
              </Stack>
            )}

            <Stack spacing={0.5} alignItems="center">
              <Typography sx={{ color: '#4d4f5b', fontWeight: 600, fontSize: 22 }}>
                {`${user.first_name} ${user.last_name}`}
              </Typography>
              {user.title && (
                <Typography variant="body2" sx={{ color: '#A2AAB8' }}>
                  {user.title}
                </Typography>
              )}
            </Stack>

            <Stack spacing={1} alignItems="center" sx={{ width: '100%' }}>
              <Typography variant="body2">
                <Box component="span" sx={{ color: '#A2AAB8', mr: 1 }}>@</Box>
                <Box component="span" sx={{ color: '#4d4f5b' }}>{user.email}</Box>
              </Typography>

              {user.phone && (
                <Typography variant="body2">
                  <Box component="span" sx={{ color: '#A2AAB8', mr: 1 }}>
                    <i className="fa-light fa-phone"></i>
                  </Box>
                  <Box component="span" sx={{ color: '#4d4f5b' }}>{user.phone}</Box>
                </Typography>
              )}

              {user.mobile_phone && (
                <Typography variant="body2">
                  <Box component="span" sx={{ color: '#A2AAB8', mr: 1 }}>
                    <i className="fa-light fa-mobile"></i>
                  </Box>
                  <Box component="span" sx={{ color: '#4d4f5b' }}>{user.mobile_phone}</Box>
                </Typography>
              )}
            </Stack>
            <Stack direction="row" justifyContent="space-between" sx={{ width: '100%' }}>
              <Typography variant="caption" sx={{ textAlign: 'left' }}>
                <Box component="span" sx={{ color: '#A2AAB8' }}>{t('profile.created_at')} : </Box>
                <Box component="span" sx={{ color: '#5D5D5D' }}>
                  {formatDate(user.created_at)}
                </Box>
              </Typography>
              <Typography variant="caption" sx={{ textAlign: 'left' }}>
                <Box component="span" sx={{ color: '#A2AAB8' }}>{t('profile.updated_at')} : </Box>
                <Box component="span" sx={{ color: '#5D5D5D' }}>
                  {formatDate(user.updated_at)}
                </Box>
              </Typography>
            </Stack>
            {
              !user.internal && (
                <Stack sx={{ width: '100%' }} mb={1}>
                  <Stack direction={'row'} justifyContent={'end'} alignItems={'center'}>
                    <Typography variant="caption" sx={{ textAlign: 'left' }}>
                      <Box component="span" sx={{ color: 'black' }} mr={1}>{t('two_fa_disclaimer.title')}</Box>
                    </Typography>
                    <Switch
                      checked={user.two_fa_activated}
                      onClick={() => {
                        if (!user.two_fa_activated) {
                          setTwoFaOpen(true)
                        }
                      }}
                      disabled={user.two_fa_activated}
                    />
                  </Stack>
                </Stack>
              )
            }
          </Stack>
        </InfoCard>
      </Stack>
    </ProfileCard>
  )
}

export default ProfileSection
